import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../CustomSelect';
import FormSection from '../FormSection';
import Modal from '../Modal';
import { formatInternationalPhoneForStorage, parseInternationalPhoneFromStorage } from '../PhoneInput';
import BusinessRoundsInterestedModal from './BusinessRoundsInterestedModal';
import {
  cancelNetworkingBooking,
  createNetworkingBooking,
  fetchMyBookingsMine,
  fetchNetworkingConfig,
  fetchNetworkingSchedule,
  NetworkingApiError,
  NETWORKING_BOOKING_LIMIT_REACHED_CODE,
  NETWORKING_PROFILE_INCOMPLETE_CODE,
  updateNetworkingBookingPartner,
  type AddBusinessRoundParticipantResult,
  type BusinessRoundInterestedParticipant,
  type MyBookingRow,
  type SlotRow,
} from './networkingApi';
import {
  CATALOGO_INICIATIVAS_PATH,
  canViewBusinessRoundsInterestedList,
  clearNetworkingCatalogResume,
  loadNetworkingCatalogResume,
  NETWORKING_COMPACT_CATALOG_MQ,
  saveNetworkingCatalogResume,
} from './networkingCatalogReturn';
import {
  emptyManualContact,
  isManualRowComplete,
  isManualRowPartial,
  PartnerModalCommonFields,
  DEFAULT_MAX_MANUAL_CONTACTS,
  type ManualContactRow,
} from './PartnerManualFields';
import './NetworkingReservations.css';

function useNetworkingDarkMode(): boolean {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark-theme')
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDark(el.classList.contains('dark-theme'));
    const mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    sync();
    return () => mo.disconnect();
  }, []);
  return dark;
}

export interface NetworkingReservationsProps {
  userEmail?: string;
}

function fmtRange(startIso: string, endIso: string) {
  const a = new Date(startIso);
  const b = new Date(endIso);
  return `${a.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} – ${b.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}`;
}

function fmtSlotTime(iso: string) {
  const t = new Date(iso);
  return t.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function fmtEsDate(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Texto del aviso de cupo; si hay más reservas que el máximo vigente, no muestra «8 de 5». */
function bookingLimitHintMessage(max: number, count: number): string {
  if (count > max) {
    return 'Alcanzaste el límite de reuniones reservadas.';
  }
  if (count >= max) {
    return `Alcanzaste el límite de ${max} reuniones reservadas.`;
  }
  return `Podés reservar hasta ${max} reuniones en total (${count} de ${max} usadas).`;
}

function bookingLimitReachedError(max: number): string {
  return `Alcanzaste el límite de ${max} reuniones reservadas.`;
}

/** Título principal de la pestaña (mismo `FormSection` / `form-section-title` que el resto del perfil). */
const NETWORKING_PAGE_TITLE = 'Agendar reuniones en el espacio de networking.';

/** Avisos fijos sobre el espacio (pantalla principal de reservas). */
const NETWORKING_KEY_NOTICES: readonly string[] = [
  'La sala de networking es prioritaria para socios empresariales, instituciones colaboradoras y gobiernos.',
];

function useNetworkingCompactCatalogNav(): boolean {
  const [compact, setCompact] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(NETWORKING_COMPACT_CATALOG_MQ).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(NETWORKING_COMPACT_CATALOG_MQ);
    const sync = () => setCompact(mql.matches);
    mql.addEventListener('change', sync);
    sync();
    return () => mql.removeEventListener('change', sync);
  }, []);
  return compact;
}

function normalizePartnerType(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

type ParsedManualPerson = {
  fullName: string;
  organization: string;
  email: string;
  phone: string;
};

/** Personas de contraparte manual tal como vienen de la reserva (JSON o columnas legacy). */
function parseManualPeopleFromBooking(b: MyBookingRow): ParsedManualPerson[] {
  if (b.manual_contacts_json) {
    try {
      const arr = JSON.parse(b.manual_contacts_json) as Array<Record<string, unknown>>;
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map((raw) => ({
          fullName: String(raw.fullName ?? (raw as { name?: string }).name ?? '').trim(),
          organization: String(
            raw.organization ?? (raw as { org?: string }).org ?? ''
          ).trim(),
          email: String(raw.email ?? '').trim(),
          phone: String(raw.phone ?? '').trim(),
        }));
      }
    } catch {
      /* ignore */
    }
  }
  const name = (b.manual_name || '').trim();
  const email = (b.manual_email || '').trim();
  const org = (b.manual_company || '').trim();
  if (name || email || org) {
    return [{ fullName: name, organization: org, email, phone: '' }];
  }
  return [];
}

/** Una línea legible nombre · organización (sin rótulos; mismo criterio en resumen y lista expandida). */
function personSummaryLine(p: ParsedManualPerson): string {
  const name = (p.fullName || '').trim();
  const org = (p.organization || '').trim();
  if (name && org) return `${name} · ${org}`;
  if (name) return name;
  if (org) return org;
  return 'Persona externa';
}

function manualPartnerSummaryFromBooking(b: MyBookingRow): string {
  const people = parseManualPeopleFromBooking(b);
  if (people.length === 0) return 'Sin definir';
  const first = people[0];
  const line = personSummaryLine(first);
  if (people.length === 1) return line;
  return `${line} (+${people.length - 1})`;
}

/** Título legible de la contraparte (nunca muestra el literal crudo de `partner_type`). */
function counterpartTitleForBooking(b: MyBookingRow): string {
  const pt = normalizePartnerType(b.partner_type);
  if (pt === 'catalog') {
    const n = (b.initiative_name_snapshot || '').trim();
    const o = (b.initiative_org_snapshot || '').trim();
    if (n && o) return `${n} · ${o}`;
    if (n) return n;
    return 'Contraparte (reserva anterior)';
  }
  if (pt === 'none') return 'Sin definir';
  if (pt === 'manual') return manualPartnerSummaryFromBooking(b);
  return manualPartnerSummaryFromBooking(b);
}

/** Bloque visible siempre: catálogo / sin definir, o primera persona manual con email y teléfono (mismo estilo que «Ver más»). */
function primaryContactDisplay(b: MyBookingRow): React.ReactNode {
  const pt = normalizePartnerType(b.partner_type);
  if (pt === 'catalog') {
    return <span className="net-booking-counterpart-summary">{counterpartTitleForBooking(b)}</span>;
  }
  if (pt === 'none') {
    return <span className="net-booking-counterpart-summary">Sin definir</span>;
  }
  const people = parseManualPeopleFromBooking(b);
  if (people.length === 0) {
    return <span className="net-booking-counterpart-summary">Sin definir</span>;
  }
  const p = people[0];
  const extra = people.length - 1;
  return (
    <div className="net-booking-primary-contact">
      <span className="net-booking-counterpart-summary">{personSummaryLine(p)}</span>
      {extra > 0 ? <span className="net-booking-counterpart-extra-count"> (+{extra})</span> : null}
      {p.email ? (
        <>
          <br />
          <span className="net-booking-contacts-list__detail">{p.email}</span>
        </>
      ) : null}
      {p.phone ? (
        <>
          <br />
          <span className="net-booking-contacts-list__detail">{p.phone}</span>
        </>
      ) : null}
    </div>
  );
}

function canEditCounterpart(b: MyBookingRow): boolean {
  const pt = normalizePartnerType(b.partner_type);
  return pt === 'manual' || pt === 'none' || pt === 'catalog';
}

function manualRowsFromBooking(b: MyBookingRow): ManualContactRow[] {
  const people = parseManualPeopleFromBooking(b);
  if (people.length === 0) return [];
  return people.map((p) => {
    const parts = parseInternationalPhoneFromStorage(p.phone);
    return {
      fullName: p.fullName,
      email: p.email,
      organization: p.organization,
      codigoPais: parts.codigoPais,
      numeroTelefono: parts.numeroTelefono,
    };
  });
}

const SLOT_MATCH_MS = 120000;

function slotStartsMatch(a: string, b: string) {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) < SLOT_MATCH_MS;
}

function slotHasMyBooking(slot: SlotRow, myBookings: MyBookingRow[]) {
  if (slot.isMine) return true;
  return myBookings.some((b) => slotStartsMatch(b.slot_start, slot.slotStart));
}


const NetworkingReservations: React.FC<NetworkingReservationsProps> = ({ userEmail }) => {
  const showBusinessRoundsInterested = canViewBusinessRoundsInterestedList(userEmail);
  const navigate = useNavigate();
  const compactCatalogNav = useNetworkingCompactCatalogNav();
  const resumePendingSlotRef = useRef<string | null>(null);
  const skipCloseModalOnDateChangeRef = useRef(false);
  /** Evita cerrar el modal de reserva al solo actualizar «Mis reuniones» (misma fecha). */
  const prevScheduleDateRef = useRef<string>('');
  const isDark = useNetworkingDarkMode();
  const netSelectOptionsClass = isDark ? 'custom-select-options-dark' : '';
  const modalContentClass = isDark ? 'admin-panel dark-theme' : '';

  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<{
    eventDates: string[];
    slotCapacity: number;
    maxBookingsPerUser: number;
  } | null>(null);
  const [myBookings, setMyBookings] = useState<MyBookingRow[]>([]);
  const [bookingLimit, setBookingLimit] = useState<{ max: number; count: number } | null>(null);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [bookingModalSlotStart, setBookingModalSlotStart] = useState<string | null>(null);
  const [manualContacts, setManualContacts] = useState<ManualContactRow[]>([]);

  /** Reintento de carga inicial de /api/config */
  const [configBootNonce, setConfigBootNonce] = useState(0);
  const [configBootError, setConfigBootError] = useState<string | null>(null);

  const [profileIncompleteModalOpen, setProfileIncompleteModalOpen] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState<number | null>(null);
  const [expandedCounterparts, setExpandedCounterparts] = useState<Record<number, boolean>>({});
  const [editPartnerBooking, setEditPartnerBooking] = useState<MyBookingRow | null>(null);
  const [editPartnerContacts, setEditPartnerContacts] = useState<ManualContactRow[]>([]);
  const [editPartnerSaving, setEditPartnerSaving] = useState(false);
  const [brrModalOpen, setBrrModalOpen] = useState(false);
  const [brrFillTarget, setBrrFillTarget] = useState<'booking' | 'edit'>('booking');

  const brrAddedContacts = brrFillTarget === 'edit' ? editPartnerContacts : manualContacts;

  const refreshMyBookings = useCallback(async () => {
    try {
      const r = await fetchMyBookingsMine();
      setMyBookings(r.data);
      const max = r.meta?.maxBookingsPerUser ?? config?.maxBookingsPerUser ?? 5;
      const count = r.meta?.bookingCount ?? r.data.length;
      setBookingLimit({ max, count });
      setError(null);
    } catch (e: unknown) {
      setMyBookings([]);
      setBookingLimit(null);
      setError(e instanceof Error ? e.message : 'Error al cargar tus reservas');
    }
  }, [config?.maxBookingsPerUser]);

  useEffect(() => {
    void refreshMyBookings();
  }, [refreshMyBookings]);

  useEffect(() => {
    let cancelled = false;
    setConfigBootError(null);
    fetchNetworkingConfig()
      .then((r) => {
        if (cancelled) return;
        setConfig(r.data);
        setDate((prev) => {
          if (prev) return prev;
          if (r.data.eventDates?.length) return r.data.eventDates[0];
          return new Date().toISOString().slice(0, 10);
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setConfigBootError(e instanceof Error ? e.message : 'Error');
      });
    return () => {
      cancelled = true;
    };
  }, [configBootNonce]);

  function resetPartnerForm() {
    setManualContacts([]);
  }

  function openBusinessRoundsInterested(target: 'booking' | 'edit') {
    setBrrFillTarget(target);
    setBrrModalOpen(true);
  }

  function applyBusinessRoundParticipant(
    participant: BusinessRoundInterestedParticipant,
    target: 'booking' | 'edit'
  ): AddBusinessRoundParticipantResult {
    const fromCatalog = (participant.sources || []).includes('catalog_initiative');
    const parsed = parseInternationalPhoneFromStorage(
      participant.hideContact || !fromCatalog ? '' : participant.phone || ''
    );
    const row: ManualContactRow = {
      fullName: participant.fullName,
      email: participant.hideContact ? '' : participant.email,
      organization: participant.organization,
      codigoPais: parsed.codigoPais || 'AR',
      numeroTelefono: parsed.numeroTelefono || '',
    };
    let added = false;
    let counterpartyNumber = 0;
    const updater = (prev: ManualContactRow[]) => {
      if (prev.length >= DEFAULT_MAX_MANUAL_CONTACTS) return prev;
      added = true;
      counterpartyNumber = prev.length + 1;
      return [...prev, row];
    };
    if (target === 'edit') {
      setEditPartnerContacts(updater);
    } else {
      setManualContacts(updater);
    }
    if (!added) {
      return { ok: false, reason: 'limit' };
    }
    return { ok: true, fullName: row.fullName.trim(), counterpartyNumber };
  }

  function closeBookingModal() {
    setBookingModalSlotStart(null);
    resetPartnerForm();
  }

  const openCatalogFromBookingModal = useCallback(() => {
    if (!bookingModalSlotStart || !date) return;
    if (compactCatalogNav) {
      saveNetworkingCatalogResume({
        date,
        slotStart: bookingModalSlotStart,
        manualContacts,
      });
      navigate(`${CATALOGO_INICIATIVAS_PATH}?returnBooking=1`);
      return;
    }
    window.open(CATALOGO_INICIATIVAS_PATH, '_blank', 'noopener,noreferrer');
  }, [bookingModalSlotStart, compactCatalogNav, date, manualContacts, navigate]);

  useEffect(() => {
    const resume = loadNetworkingCatalogResume();
    if (!resume) return;
    clearNetworkingCatalogResume();
    skipCloseModalOnDateChangeRef.current = true;
    resumePendingSlotRef.current = resume.slotStart;
    setManualContacts(resume.manualContacts);
    setDate(resume.date);
  }, []);

  useEffect(() => {
    const pending = resumePendingSlotRef.current;
    if (!pending || loading) return;
    const row = slots.find((s) => slotStartsMatch(s.slotStart, pending));
    resumePendingSlotRef.current = null;
    if (row && !row.booked && !slotHasMyBooking(row, myBookings)) {
      setBookingModalSlotStart(row.slotStart);
    }
  }, [slots, loading, myBookings]);

  useEffect(() => {
    if (!date) return;
    const dateChanged = prevScheduleDateRef.current !== date;
    prevScheduleDateRef.current = date;

    if (dateChanged) {
      setLoading(true);
      if (!skipCloseModalOnDateChangeRef.current) {
        setBookingModalSlotStart(null);
      }
    }
    skipCloseModalOnDateChangeRef.current = false;

    let cancelled = false;
    fetchNetworkingSchedule(date)
      .then((r) => {
        if (!cancelled) setSlots(r.data);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date, myBookings]);

  const eventDaySubtitle = useMemo(() => {
    const dates = config?.eventDates ?? [];
    if (dates.length >= 2) {
      const a = fmtEsDate(dates[0]);
      const b = fmtEsDate(dates[dates.length - 1]);
      return dates.length === 2 ? `Elegí entre ${a} y ${b}` : `Días del evento: desde ${a} hasta ${b}`;
    }
    if (dates.length === 1) return fmtEsDate(dates[0]);
    return 'Definí el día con el campo de fecha o configurá NETWORKING_EVENT_DATES en el servidor.';
  }, [config?.eventDates]);

  const dateSelectOptions = useMemo(
    () => (config?.eventDates ?? []).map((d) => ({ value: d, label: fmtEsDate(d) })),
    [config?.eventDates]
  );

  const maxBookingsPerUser = bookingLimit?.max ?? config?.maxBookingsPerUser ?? 5;
  const bookingCount = bookingLimit?.count ?? myBookings.length;
  const atBookingLimit = bookingCount >= maxBookingsPerUser;

  async function confirmBooking() {
    if (!bookingModalSlotStart || !date) return;
    if (atBookingLimit) {
      setError(bookingLimitReachedError(maxBookingsPerUser));
      return;
    }
    const row = slots.find((s) => s.slotStart === bookingModalSlotStart);
    if (!row || row.booked || slotHasMyBooking(row, myBookings)) return;
    setError(null);
    try {
      const completeManualRows = manualContacts.filter(isManualRowComplete).map((c) => ({
        fullName: c.fullName.trim(),
        email: c.email.trim(),
        phone: formatInternationalPhoneForStorage(c.codigoPais, c.numeroTelefono),
        organization: c.organization.trim(),
      }));
      const effectivePartnerType = completeManualRows.length > 0 ? 'manual' : 'none';

      await createNetworkingBooking({
        date,
        slotStart: row.slotStart,
        partnerType: effectivePartnerType,
        manualContacts: effectivePartnerType === 'manual' ? completeManualRows : undefined,
      });
      closeBookingModal();
      await refreshMyBookings();
      const sch = await fetchNetworkingSchedule(date);
      setSlots(sch.data);
    } catch (e: unknown) {
      const profileIncomplete =
        e instanceof NetworkingApiError &&
        (e.code === NETWORKING_PROFILE_INCOMPLETE_CODE ||
          (/^Completá /i.test(e.message) && e.message.toLowerCase().includes('perfil')));
      if (profileIncomplete) {
        closeBookingModal();
        setProfileIncompleteModalOpen(true);
        return;
      }
      if (
        e instanceof NetworkingApiError &&
        e.code === NETWORKING_BOOKING_LIMIT_REACHED_CODE
      ) {
        closeBookingModal();
      }
      setError(e instanceof Error ? e.message : 'Error');
    }
  }

  function closeEditPartnerModal() {
    setEditPartnerBooking(null);
    setEditPartnerContacts([]);
    setEditPartnerSaving(false);
  }

  function openEditPartnerModal(b: MyBookingRow) {
    const rows = manualRowsFromBooking(b);
    setEditPartnerBooking(b);
    setEditPartnerContacts(rows.length > 0 ? rows : [emptyManualContact()]);
  }

  async function saveEditPartner() {
    if (!editPartnerBooking) return;
    const id = editPartnerBooking.id;
    if (editPartnerContacts.some(isManualRowPartial)) {
      setError('Completá nombre completo, organización y un correo válido (si lo cargás) en cada contraparte, o quitá las filas incompletas.');
      return;
    }
    setError(null);
    setEditPartnerSaving(true);
    try {
      const completeManualRows = editPartnerContacts.filter(isManualRowComplete).map((c) => ({
        fullName: c.fullName.trim(),
        email: c.email.trim(),
        phone: formatInternationalPhoneForStorage(c.codigoPais, c.numeroTelefono),
        organization: c.organization.trim(),
      }));
      const effectivePartnerType = completeManualRows.length > 0 ? 'manual' : 'none';
      await updateNetworkingBookingPartner(id, {
        partnerType: effectivePartnerType,
        manualContacts: effectivePartnerType === 'manual' ? completeManualRows : undefined,
      });
      closeEditPartnerModal();
      await refreshMyBookings();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally {
      setEditPartnerSaving(false);
    }
  }

  const performCancelBooking = useCallback(async () => {
    const id = cancelBookingId;
    if (id == null) return;
    setCancelBookingId(null);
    setError(null);
    try {
      await cancelNetworkingBooking(id);
      await refreshMyBookings();
      if (date) {
        const sch = await fetchNetworkingSchedule(date);
        setSlots(sch.data);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  }, [cancelBookingId, date, refreshMyBookings]);

  if (!config) {
    return (
      <div className="profile-networking-embed">
        <FormSection title={NETWORKING_PAGE_TITLE}>
          {configBootError ? (
            <div className="profile-networking-loading" role="alert">
              <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>No se pudo cargar networking</p>
              <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>{configBootError}</p>
              <button type="button" className="save-profile-button" onClick={() => setConfigBootNonce((n) => n + 1)}>
                Reintentar
              </button>
            </div>
          ) : (
            <div className="profile-networking-loading" role="status">
              Cargando reservas de networking…
            </div>
          )}
        </FormSection>
      </div>
    );
  }

  return (
    <div className="profile-networking-embed">
      <FormSection title={NETWORKING_PAGE_TITLE}>
        <div className="networking-app">
          <main className="net-main">
          {error && <div className="auth-message error net-alert">{error}</div>}

          <div className="net-key-notices" role="region" aria-label="Información sobre el espacio de networking">
            {NETWORKING_KEY_NOTICES.map((text, i) => (
              <p key={i} className="net-key-notices__item">
                {text}
              </p>
            ))}
          </div>
          <p className="net-reservation-profile-hint">
            Para poder reservar una reunión tenés que tener completos todos los campos obligatorios del perfil personal.
          </p>
          <p className="net-reservation-profile-hint">
            {bookingLimitHintMessage(maxBookingsPerUser, bookingCount)}
          </p>

          <div className="net-layout">
            <aside className="net-sidebar" aria-label="Tus reuniones agendadas">
              <section className="net-card net-card--sidebar">
                <h2 className="net-section-title">Mis reuniones</h2>
                <ul className="net-booking-list net-booking-list--scroll">
                  {(myBookings || []).map((b) => {
                    const counterpartPeople = parseManualPeopleFromBooking(b);
                    const extraPeople = counterpartPeople.length > 1 ? counterpartPeople.slice(1) : [];
                    const extraCount = extraPeople.length;
                    return (
                    <li key={b.id} className="net-booking-item net-booking-item--stack">
                      <div className="net-booking-line">
                        <strong className="net-booking-time">
                          <span className="net-booking-day-label">
                            {fmtEsDate(b.slot_start.slice(0, 10))}
                          </span>
                          <span className="net-booking-datetime-sep" aria-hidden="true">
                            {' · '}
                          </span>
                          <span>{fmtRange(b.slot_start, b.slot_end)}</span>
                        </strong>
                      </div>
                      <div className="net-booking-meta net-booking-counterpart net-booking-item-body">
                        <span className="net-booking-counterpart-section-label">Contraparte</span>
                        {primaryContactDisplay(b)}
                        {extraCount > 0 && (
                          <button
                            type="button"
                            className="net-booking-link-btn"
                            onClick={() =>
                              setExpandedCounterparts((prev) => ({
                                ...prev,
                                [b.id]: !prev[b.id],
                              }))
                            }
                          >
                            {expandedCounterparts[b.id]
                              ? 'Ocultar contrapartes'
                              : extraCount === 1
                                ? 'Ver 1 contraparte más'
                                : `Ver ${extraCount} contrapartes más`}
                          </button>
                        )}
                        {expandedCounterparts[b.id] && extraCount > 0 && (
                          <ul className="net-booking-contacts-list">
                            {extraPeople.map((p, i) => (
                              <li key={i} className="net-booking-contacts-list__person">
                                <span className="net-booking-counterpart-summary">{personSummaryLine(p)}</span>
                                {p.email ? (
                                  <>
                                    <br />
                                    <span className="net-booking-contacts-list__detail">{p.email}</span>
                                  </>
                                ) : null}
                                {p.phone ? (
                                  <>
                                    <br />
                                    <span className="net-booking-contacts-list__detail">{p.phone}</span>
                                  </>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="net-booking-item-toolbar">
                        {canEditCounterpart(b) && (
                          <button
                            type="button"
                            className="save-profile-button net-btn-compact net-booking-toolbar-btn"
                            onClick={() => openEditPartnerModal(b)}
                          >
                            Editar
                          </button>
                        )}
                        <button
                          type="button"
                          className="logout-button net-btn-compact net-booking-toolbar-btn"
                          onClick={() => setCancelBookingId(b.id)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </li>
                    );
                  })}
                </ul>
              </section>
            </aside>

            <div className="net-primary">
              <section className="net-card net-card--stretch">
                <h3 className="net-subsection-title">Días</h3>
                <p className="net-schedule-head-sub">{eventDaySubtitle}</p>

                <div className="net-date-block">
                  {config.eventDates?.length ? (
                    <CustomSelect
                      id="net-date-select"
                      className="net-day-select"
                      optionsClassName={netSelectOptionsClass}
                      value={date}
                      onChange={(v) => setDate(v)}
                      options={dateSelectOptions}
                    />
                  ) : (
                    <input
                      id="net-date-input"
                      className="form-input net-day-select"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  )}
                </div>

                <div className="net-horarios-block">
                  <h3 className="net-subsection-title">Horarios</h3>
                  {loading && <p className="net-loading">Cargando franjas…</p>}
                  <div className="mesa-floor-wrap">
                    <div className="mesa-floor" role="group" aria-label="Franjas horarias disponibles para reservar">
                      {slots.map((s) => {
                        const mine = slotHasMyBooking(s, myBookings);
                        const disabled = s.booked || mine || (atBookingLimit && !mine);
                        const selected = bookingModalSlotStart === s.slotStart;
                        const statusLabel = mine
                          ? 'Tu reunión'
                          : atBookingLimit && !s.booked
                            ? 'Límite alcanzado'
                            : s.booked
                              ? 'Sin cupos'
                              : 'Disponible';
                        return (
                          <button
                            key={s.slotStart}
                            type="button"
                            disabled={disabled}
                            className={`mesa-pad ${selected ? 'mesa-pad--selected' : ''} ${s.booked ? 'mesa-pad--booked' : ''} ${mine ? 'mesa-pad--mine' : ''} ${atBookingLimit && !mine && !s.booked ? 'mesa-pad--limit' : ''}`}
                            aria-pressed={selected}
                            aria-label={`${fmtRange(s.slotStart, s.slotEnd)}. ${statusLabel}.`}
                            onClick={() => {
                              if (atBookingLimit && !mine) {
                                setError(bookingLimitReachedError(maxBookingsPerUser));
                                return;
                              }
                              if (bookingModalSlotStart === s.slotStart) {
                                closeBookingModal();
                              } else {
                                resetPartnerForm();
                                setBookingModalSlotStart(s.slotStart);
                              }
                            }}
                          >
                            <span className="mesa-pad-times">
                              <span className="mesa-pad-time mesa-pad-time--start">{fmtSlotTime(s.slotStart)}</span>
                              <span className="mesa-pad-time mesa-pad-time--end">{fmtSlotTime(s.slotEnd)}</span>
                            </span>
                            <span className="mesa-pad-label">{statusLabel}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {!bookingModalSlotStart && !loading && slots.length === 0 && (
                    <p className="mesa-floor-placeholder">No hay franjas para esta fecha.</p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </main>

        {bookingModalSlotStart &&
          slots.some((s) => s.slotStart === bookingModalSlotStart) && (
          <div className="net-modal-backdrop" role="presentation" onClick={() => closeBookingModal()}>
            <div
              className="net-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="net-booking-dialog-title"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const pickedRow = slots.find((s) => s.slotStart === bookingModalSlotStart);
                const slotOk = !!(
                  pickedRow &&
                  !pickedRow.booked &&
                  !slotHasMyBooking(pickedRow, myBookings)
                );
                const partnerOk = !manualContacts.some(isManualRowPartial);
                const canSubmit = slotOk && partnerOk && !atBookingLimit;
                return (
                  <>
                    <h2 id="net-booking-dialog-title">Reservar reunión</h2>
                    <p className="net-modal-summary">
                      <strong>{pickedRow ? fmtRange(pickedRow.slotStart, pickedRow.slotEnd) : ''}</strong> ·{' '}
                      {fmtEsDate(date)}
                    </p>
                    <div className="net-modal-fields">
                      <PartnerModalCommonFields
                        contacts={manualContacts}
                        setContacts={setManualContacts}
                        fieldIdPrefix="net-manual"
                        onNavigateToCatalog={compactCatalogNav ? openCatalogFromBookingModal : undefined}
                        onViewBusinessRoundsInterested={
                          showBusinessRoundsInterested ? () => openBusinessRoundsInterested('booking') : undefined
                        }
                      />

                      <div className="net-modal-actions">
                        <button type="button" className="logout-button" onClick={() => closeBookingModal()}>
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="save-profile-button"
                          disabled={!canSubmit}
                          onClick={() => confirmBooking()}
                        >
                          Confirmar reserva
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      <BusinessRoundsInterestedModal
        isOpen={brrModalOpen}
        onClose={() => setBrrModalOpen(false)}
        contentClassName={modalContentClass}
        addedContacts={brrAddedContacts}
        canAddParticipant={brrAddedContacts.length < DEFAULT_MAX_MANUAL_CONTACTS}
        onSelectParticipant={(p) => applyBusinessRoundParticipant(p, brrFillTarget)}
      />

      <Modal
        isOpen={profileIncompleteModalOpen}
        onClose={() => setProfileIncompleteModalOpen(false)}
        title="Perfil personal incompleto"
        onConfirm={() => setProfileIncompleteModalOpen(false)}
        confirmText="Entendido"
        cancelText=""
        contentClassName={modalContentClass}
      >
        <p style={{ margin: 0 }}>
          Para poder hacer una reserva tenés que completar todos los campos obligatorios del perfil personal.
        </p>
      </Modal>

      <Modal
        isOpen={cancelBookingId != null}
        onClose={() => setCancelBookingId(null)}
        title="¿Cancelar esta reserva?"
        onConfirm={() => void performCancelBooking()}
        confirmText="Sí, cancelar"
        cancelText="No"
        contentClassName={modalContentClass}
      >
        <p style={{ margin: 0 }}>
          Vas a cancelar tu reserva en esa franja. Si cambiás de idea, vas a tener que volver a reservar si el horario
          sigue disponible.
        </p>
      </Modal>

      {editPartnerBooking && (
        <div
          className="net-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!editPartnerSaving) closeEditPartnerModal();
          }}
        >
          <div
            className="net-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="net-edit-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const partnerOk = !editPartnerContacts.some(isManualRowPartial);
              const canSaveEdit = partnerOk;
              return (
                <>
                  <h2 id="net-edit-dialog-title">Editar datos de la contraparte</h2>
                  <p className="net-modal-summary">
                    <strong>{fmtRange(editPartnerBooking.slot_start, editPartnerBooking.slot_end)}</strong> ·{' '}
                    {fmtEsDate(editPartnerBooking.slot_start.slice(0, 10))}
                  </p>
                  <div className="net-modal-fields">
                    <PartnerModalCommonFields
                      contacts={editPartnerContacts}
                      setContacts={setEditPartnerContacts}
                      fieldIdPrefix="net-edit-manual"
                      onViewBusinessRoundsInterested={
                        showBusinessRoundsInterested ? () => openBusinessRoundsInterested('edit') : undefined
                      }
                    />
                    <div className="net-modal-actions">
                      <button
                        type="button"
                        className="logout-button"
                        disabled={editPartnerSaving}
                        onClick={() => closeEditPartnerModal()}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="save-profile-button"
                        disabled={editPartnerSaving || !canSaveEdit}
                        onClick={() => void saveEditPartner()}
                      >
                        {editPartnerSaving ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      </FormSection>
    </div>
  );
};

export default NetworkingReservations;
